import type {
  NotOnSheetRow,
  PricingResult,
  PricingRow,
  PricingUpdate,
} from './types';

const FLAG_LABELS: Record<string, string> = {
  marker: 'Marked on sheet',
  existing_discount: 'Has a discount',
  conflict: 'Conflict',
  partial_replace: 'Ends a shared row',
  overlaps_new: 'Overlaps a new row',
};

/** A flag or block code as the card's tag says it; an unknown one spelled out. */
export const flagLabel = (code: string): string =>
  Object.prototype.hasOwnProperty.call(FLAG_LABELS, code)
    ? FLAG_LABELS[code]
    : code.replace(/_/g, ' ');

/** Rows the CA can tick: not blocked, and not already at the sheet's rate. */
export const canTick = (row: PricingRow | NotOnSheetRow): boolean =>
  row.blocked.length === 0 && !('class' in row && row.class === 'unchanged');

const everyRow = (u: PricingUpdate): (PricingRow | NotOnSheetRow)[] => [
  ...u.sections.flatMap((s) => s.rows),
  ...u.notOnSheet,
];

/** The rows the service ticked, minus any that can't be ticked. */
export const initialSelection = (u: PricingUpdate): Set<string> =>
  new Set(
    everyRow(u)
      .filter((r) => r.ticked && canTick(r))
      .map((r) => r.id),
  );

export const toggle = (sel: Set<string>, id: string): Set<string> => {
  const next = new Set(sel);
  if (!next.delete(id)) next.add(id);
  return next;
};

/**
 * The section's checkbox: unticks every row when all its tickable rows are
 * ticked, otherwise ticks them all. Blocked rows are never ticked.
 */
export const toggleSection = (
  u: PricingUpdate,
  sel: Set<string>,
  sectionIndex: number,
): Set<string> => {
  const ids = (u.sections[sectionIndex]?.rows ?? [])
    .filter(canTick)
    .map((r) => r.id);
  const next = new Set(sel);
  const allTicked = ids.length > 0 && ids.every((id) => next.has(id));
  for (const id of ids) {
    if (allTicked) next.delete(id);
    else next.add(id);
  }
  return next;
};

/** Whether a section's rows are all ticked / some ticked, for its header checkbox. */
export const sectionTicks = (
  u: PricingUpdate,
  sel: Set<string>,
  sectionIndex: number,
): { all: boolean; some: boolean } => {
  const ids = (u.sections[sectionIndex]?.rows ?? [])
    .filter(canTick)
    .map((r) => r.id);
  const ticked = ids.filter((id) => sel.has(id)).length;
  return { all: ids.length > 0 && ticked === ids.length, some: ticked > 0 };
};

/** The ids Confirm sends: only ids on the card, and only ones that can be ticked. */
export const selectedIds = (u: PricingUpdate, sel: Set<string>): string[] =>
  everyRow(u)
    .filter((r) => sel.has(r.id) && canTick(r))
    .map((r) => r.id);

export const selectionCounts = (
  u: PricingUpdate,
  sel: Set<string>,
): { add: number; change: number; end: number } => {
  const rows = u.sections.flatMap((s) => s.rows).filter((r) => sel.has(r.id));
  return {
    add: rows.filter((r) => r.class === 'new').length,
    change: rows.filter((r) => r.class === 'changed').length,
    end: u.notOnSheet.filter((r) => sel.has(r.id)).length,
  };
};

/** "7.140" -> "7.14", "7" -> "7.00": trailing zeros go, two decimals stay. */
export const fmtRate = (rate: string): string => {
  const n = Number(rate);
  if (!Number.isFinite(n)) return rate;
  const trimmed = String(n);
  const decimals = trimmed.includes('.') ? trimmed.split('.')[1].length : 0;
  return n.toFixed(Math.max(2, decimals));
};

export function rateText(row: PricingRow): string {
  const next = row.newRate === null ? null : fmtRate(row.newRate);
  const old = row.oldRate === null ? null : fmtRate(row.oldRate);
  if (row.class === 'unchanged') {
    return `${next ?? old ?? '?'}% (no change)`;
  }
  if (old === null || row.class === 'new') {
    return `new ${next ?? '?'}%${row.derivedFrom ? ` (${row.derivedFrom})` : ''}`;
  }
  return `${old}% → ${next ?? '?'}%${row.derivedFrom ? ` (${row.derivedFrom})` : ''}`;
}

/** The closed card's one line. */
export const resultText = (r: PricingResult): string =>
  r.status === 'discarded'
    ? 'Discarded: nothing was changed.'
    : `Applied: ${r.added} added, ${r.ended} ended, ${r.unchanged} unchanged, ${r.skipped} skipped, ${r.seconds} s from drop to applied`;
